// Validates an opener-supplied returnOrigin against this environment's allow-list
// before it is ever trusted as a postMessage target. Fails closed: missing config,
// a non-https scheme, or no suffix match all reject.

// A suffix with no leading "." would match a hostname that merely contains it with no
// subdomain boundary (e.g. configuring "mspprocess.net" would also match
// "evilmspprocess.net"), so every configured suffix is normalised to carry one here, at
// load time, rather than trusting each value in the env file to already have it.
const normalizeSuffix = (suffix: string): string => (suffix.startsWith('.') ? suffix : `.${suffix}`);

const ALLOWED_SUFFIXES = (import.meta.env.VITE_ALLOWED_OPENER_ORIGINS ?? '')
  .split(';')
  .map((suffix: string) => suffix.trim())
  .filter((suffix: string) => suffix.length > 0)
  .map(normalizeSuffix);

export const isAllowedOpenerOrigin = (
  candidate: string | null | undefined,
  allowedSuffixes: string[] = ALLOWED_SUFFIXES,
): boolean => {
  if (!candidate || allowedSuffixes.length === 0) {
    return false;
  }

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'https:') {
    return false;
  }

  const hostname = parsed.hostname.toLowerCase();
  return allowedSuffixes.map(normalizeSuffix).some(suffix => {
    const normalized = suffix.toLowerCase();
    return hostname === normalized.slice(1) || hostname.endsWith(normalized);
  });
};

export const validateReturnOrigin = (candidate: string | null): string | null =>
  isAllowedOpenerOrigin(candidate) ? candidate : null;
